/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role -- ARIA combobox popups use listbox semantics while the input retains keyboard focus. */
import { i18n } from "@gnu-taler/taler-util";
import { Fragment, VNode, h } from "preact";
import { useId, useRef, useState } from "preact/hooks";
import { resolveFieldHandler, UIFormProps } from "../FormProvider.js";
import { ChoiceS } from "./InputChoiceStacked.js";
import { LabelWithTooltipMaybeRequired } from "./InputLine.js";

export interface InputSelectOneProps<Choices> {
  preferredChoiceVals?: Choices[];
  choices: ChoiceS<Choices>[];
}

export function InputSelectOne<Choices>(
  props: InputSelectOneProps<Choices> & UIFormProps<Choices>,
): VNode {
  const { label, choices, placeholder, tooltip, required, help, hidden } =
    props;
  const { value, onChange, error } = resolveFieldHandler(
    props.handler,
    props.name,
  );

  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dirty, setDirty] = useState<boolean>(); // FIXME: dirty state should come from handler
  const id = `select-one-${useId()}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedFilter = (filter ?? "").toLocaleLowerCase();
  const choiceMap = choices.reduce(
    (prev, curr) => {
      return { ...prev, [curr.value as string]: curr.label };
    },
    {} as Record<string, string>,
  );
  if (hidden) {
    return <Fragment />;
  }

  const prefChoices = choices.filter(
    (x) =>
      props.preferredChoiceVals && props.preferredChoiceVals.includes(x.value),
  );
  const normalChoices = choices.filter(
    (x) =>
      !(
        props.preferredChoiceVals && props.preferredChoiceVals.includes(x.value)
      ),
  );

  const sortedChoices = [...prefChoices, ...normalChoices];

  let filteredChoices =
    filter === undefined
      ? undefined
      : sortedChoices.filter((v) => {
          return [v.label, v.description].some((text) =>
            text?.toLocaleLowerCase().includes(normalizedFilter),
          );
        });

  const noItems =
    filter === undefined
      ? undefined
      : filteredChoices === undefined || !filteredChoices.length;
  const activeChoiceIndex = Math.min(
    activeIndex,
    (filteredChoices?.length ?? 0) - 1,
  );
  return (
    <div class="sm:col-span-6">
      <LabelWithTooltipMaybeRequired
        label={label}
        technicalName={props.technicalName}
        required={required}
        tooltip={tooltip}
        name={id}
      />
      {value !== undefined ? (
        <span class="mr-2 inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 p-1 font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-200">
          {choiceMap[value as string]}
          <button
            type="button"
            disabled={props.disabled}
            onClick={() => {
              onChange(undefined!);
              setDirty(true);
            }}
            class="group relative h-5 w-5 rounded-sm hover:bg-gray-500/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed"
            aria-label={i18n.str`Clear selection`}
          >
            <svg
              viewBox="0 0 14 14"
              class="h-5 w-5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
            >
              <path d="M4 4l6 6m0-6l-6 6" />
            </svg>
            <span class="absolute -inset-1"></span>
          </button>
        </span>
      ) : (
        <div class="relative mt-2">
          <input
            id={id}
            autocomplete="off"
            ref={inputRef}
            type="text"
            value={filter ?? ""}
            disabled={props.disabled}
            onInput={(e) => {
              setFilter(e.currentTarget.value);
              setActiveIndex(0);
              setDirty(true);
            }}
            onKeyDown={(event) => {
              if (filter === undefined) return;
              if (event.key === "Escape") {
                event.preventDefault();
                setFilter(undefined);
                return;
              }
              // Searching must not submit the surrounding form, including
              // when no option matches the query.
              if (event.key === "Enter") event.preventDefault();
              if (!filteredChoices?.length) return;
              switch (event.key) {
                case "ArrowDown":
                  event.preventDefault();
                  setActiveIndex((current) =>
                    Math.min(current + 1, filteredChoices.length - 1),
                  );
                  break;
                case "ArrowUp":
                  event.preventDefault();
                  setActiveIndex(Math.max(activeChoiceIndex - 1, 0));
                  break;
                case "Enter":
                  event.preventDefault();
                  setFilter(undefined);
                  onChange(filteredChoices[activeChoiceIndex].value as any);
                  setDirty(true);
                  break;
              }
            }}
            onBlur={(e) => {
              setFilter(undefined);
            }}
            onFocus={(e) => {
              setFilter("");
              setActiveIndex(0);
            }}
            placeholder={placeholder}
            class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-gray-950 dark:text-gray-100 dark:ring-gray-600 sm:text-sm sm:leading-6"
            role="combobox"
            aria-controls={`${id}-options`}
            aria-expanded={filter !== undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              filteredChoices?.length
                ? `${id}-option-${activeChoiceIndex}`
                : undefined
            }
            aria-describedby={
              [
                help ? `${id}-description` : undefined,
                error ? `${id}-error` : undefined,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
          />
          <button
            type="button"
            disabled={props.disabled}
            onMouseDown={(e) => {
              // Input element should not lose focus
              e.preventDefault();
            }}
            onClick={() => {
              setFilter(filter === undefined ? "" : undefined);
              setDirty(true);
              inputRef.current?.focus();
            }}
            class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
            aria-label={i18n.str`Toggle choices`}
          >
            <svg
              class="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          {noItems && (
            <ul
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-950 dark:ring-gray-700 sm:text-sm"
              id={`${id}-options`}
              role="listbox"
            >
              <li
                class="px-3 py-2 text-gray-500 dark:text-gray-400"
                role="option"
                aria-disabled="true"
                aria-selected="false"
              >
                <span class="block font-medium">
                  <i18n.Translate>No element found</i18n.Translate>
                </span>
              </li>
            </ul>
          )}
          {!noItems && filteredChoices && (
            <ul
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-950 dark:ring-gray-700 sm:text-sm"
              id={`${id}-options`}
              role="listbox"
            >
              {filteredChoices.map((v, idx) => {
                const active = idx === activeChoiceIndex;
                return (
                  <li key={String(v.value)} role="none">
                    <button
                      type="button"
                      class={`group relative w-full cursor-pointer select-none px-3 py-2 text-left hover:bg-indigo-600 hover:text-white ${
                        active
                          ? "bg-indigo-600 text-white"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                      id={`${id}-option-${idx}`}
                      role="option"
                      aria-selected={active}
                      aria-describedby={
                        v.description
                          ? `${id}-option-${idx}-description`
                          : undefined
                      }
                      data-active={active}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => {
                        // Input element should not lose focus
                        e.preventDefault();
                      }}
                      onClick={() => {
                        setFilter(undefined);
                        onChange(v.value as any);
                        setDirty(true);
                      }}
                    >
                      <span class="block break-words font-medium">
                        {v.label}
                      </span>
                      {v.description ? (
                        <span
                          id={`${id}-option-${idx}-description`}
                          class={`mt-0.5 block break-words text-xs group-hover:text-indigo-100 ${
                            active
                              ? "text-indigo-100"
                              : "text-gray-500 dark:text-gray-400"
                          }`}
                        >
                          {v.description}
                        </span>
                      ) : undefined}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {help && (
        <p class="mt-2 text-sm text-gray-500" id={`${id}-description`}>
          {help}
        </p>
      )}
      {dirty !== undefined && error && (
        <p class="mt-2 text-sm text-red-600" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
